import { useSearchParams } from 'next/navigation';
import { PropsWithChildren } from 'react';
import { Provider } from 'react-redux';

import { useStore } from '@/hooks/store/useStore';
import { AppInitialState } from '@/store';
import { initPurchaseParams, PurchaseQueryParams } from '@/utils/getInitialState';

const StoreProvider = ({
	children,
	initialState,
}: PropsWithChildren<{
	initialState: AppInitialState;
}>) => {
	const searchParams = useSearchParams();

	// `StoreProvider` also mounts every pages-router page via `_app.tsx`, including
	// ones with no `getServerSideProps` (e.g. signup, login, premium-confirmation) —
	// those never populate `initialState.purchaseParams` server-side, so this is the
	// only thing computing it for them. App-router pages get purchaseParams from
	// `loadInitialState` unconditionally, so this branch never fires for them.
	const purchaseParams =
		initialState?.purchaseParams ??
		initPurchaseParams({
			query: {
				plan: searchParams?.get('plan'),
				trialLength: searchParams?.get('trialLength'),
				utm_medium: searchParams?.get('utm_medium'),
				coupon: searchParams?.get('coupon'),
				promotion: searchParams?.get('promotion'),
				guestPass: searchParams?.get('guestPass'),
				calmjwt: searchParams?.get('calmjwt'),
				is30DayOverride: searchParams?.get('is30DayOverride'),
			} satisfies PurchaseQueryParams,
			prices: initialState?.prices ?? null,
			user: initialState?.user ?? null,
			device: initialState?.device,
		});

	const store = useStore({ ...initialState, purchaseParams });

	return <Provider store={store}>{children}</Provider>;
};

export default StoreProvider;
